"use client";

import React from "react";
import { ArrowRight, CheckCircle2, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON, TERMS } from "@/lib/commonCopy";
import { siteImage } from "@/lib/siteImages";
export default function SelectionConsiderations() {
  const {
    lang
  } = useLanguage();
  const eyebrowLabel = pick(lang, {
    en: "Technical Guidance",
    zh: "选型指导",
    es: "Guía técnica",
    de: "Technische Richtlinie",
    fr: "Guide technique",
    pt: "Orientação técnica"
  });
  const headingText = pick(lang, {
    en: "Selection Considerations",
    zh: "选型考虑因素",
    es: "Consideraciones de selección",
    de: "Auswahlkriterien",
    fr: "Critères de sélection",
    pt: "Considerações de seleção"
  });
  const leadText = pick(lang, {
    en: "The right filtration solution is selected based on filtration media, particle size, aperture, material, and operating temperature and pressure conditions.",
    zh: "根据过滤介质、颗粒大小、孔径、材质以及工作温度与压力条件，综合选定最适配的过滤解决方案。",
    es: "La solución de filtración adecuada se selecciona en función del medio filtrante, tamaño de partícula, apertura, material y condiciones de temperatura y presión operativas.",
    de: "Die passende Filtrationslösung wird anhand von Filtermedium, Partikelgröße, Maschenweite, Werkstoff sowie Betriebs-temperatur- und -druckbedingungen ausgewählt.",
    fr: "La solution de filtration appropriée est sélectionnée selon le média filtrant, la granulométrie, l'ouverture de maille, le matériau et les conditions de température et de pression de service.",
    pt: "A solução de filtração ideal é selecionada com base no meio filtrante, tamanho de partícula, abertura, material e condições operacionais de temperatura e pressão."
  });
  const criteria = [{
    title: pick(lang, TERMS.filtration),
    subtitle: pick(lang, {
      en: "Media & structure type",
      zh: "过滤介质与结构形式",
      es: "Tipo de medio y estructura",
      de: "Medium & Strukturtyp",
      fr: "Type de média et de structure",
      pt: "Tipo de meio e estrutura"
    }),
    desc: pick(lang, {
      en: "Woven wire mesh, multilayer sintered laminates, or wedge wire screens matched to fluid dynamics.",
      zh: "根据流体动力学条件匹配编织丝网、多层烧结复合网或楔形丝筛网结构。",
      es: "Malla de alambre tejida, laminados sinterizados multicapa o tamices de alambre en cuña adaptados a la dinámica de fluidos.",
      de: "Drahtgewebe, mehrlagige Sinterlaminate oder Spaltsiebe, abgestimmt auf die Fluiddynamik.",
      fr: "Toile métallique tissée, stratifiés frittés multicouches ou crépines à fil profilé adaptés à la dynamique des fluides.",
      pt: "Malha de arame tecida, laminados sinterizados multicamadas ou telas de arame em cunha adaptadas à dinâmica de fluidos."
    })
  }, {
    title: pick(lang, TERMS.aperture),
    subtitle: pick(lang, {
      en: "Particle size & rating",
      zh: "颗粒尺寸与截留精度",
      es: "Tamaño de partícula y grado",
      de: "Partikelgröße & Abscheidegrad",
      fr: "Granulométrie et seuil",
      pt: "Tamanho de partícula e retenção"
    }),
    desc: pick(lang, {
      en: "Nominal vs absolute micron ratings (1 µm to 25 mm) engineered to target retention efficiencies.",
      zh: "名义与绝对微米过滤等级（1 µm 至 25 mm），精确满足不同颗粒截留效率指标。",
      es: "Clasificación micrométrica nominal vs absoluta (1 µm a 25 mm) diseñada para eficiencias de retención objetivo.",
      de: "Nominale vs. absolute Mikron-Filterfeinheiten (1 µm bis 25 mm), ausgelegt auf Zielabscheidegrade.",
      fr: "Seuils nominaux vs absolus (1 µm à 25 mm) conçus pour cibler l'efficacité de rétention.",
      pt: "Classificações em mícrons nominais vs absolutas (1 µm a 25 mm) projetadas para eficiências de retenção específicas."
    })
  }, {
    title: pick(lang, TERMS.material),
    subtitle: pick(lang, {
      en: "Corrosion & temperature",
      zh: "耐腐蚀与耐温环境",
      es: "Corrosión y temperatura",
      de: "Korrosion & Temperatur",
      fr: "Corrosion et température",
      pt: "Corrosão e temperatura"
    }),
    desc: pick(lang, {
      en: "AISI 304, 316L, Hastelloy, Monel, and Inconel alloys for aggressive chemical environments.",
      zh: "选用 304、316L、哈氏合金、蒙乃尔及因科镍合金，应对酸碱及高温腐蚀工况。",
      es: "Aleaciones AISI 304, 316L, Hastelloy, Monel e Inconel para entornos químicos agresivos.",
      de: "Legierungen wie AISI 304, 316L, Hastelloy, Monel und Inconel für aggressive chemische Umgebungen.",
      fr: "Alliages AISI 304, 316L, Hastelloy, Monel et Inconel pour environnements chimiques agressifs.",
      pt: "Ligas AISI 304, 316L, Hastelloy, Monel e Inconel para ambientes químicos agressivos."
    })
  }, {
    title: pick(lang, TERMS.pressureDrop),
    subtitle: pick(lang, {
      en: "Flow rate & burst limits",
      zh: "工作压差与流量上限",
      es: "Caudal y límites de rotura",
      de: "Durchfluss & Berstdruckgrenzen",
      fr: "Débit et limites de rupture",
      pt: "Vazão e limites de ruptura"
    }),
    desc: pick(lang, {
      en: "Calculated structural rigidity, open area percentages, and reinforced pleating or support frames.",
      zh: "核算结构刚度、开孔率比例及加固褶皱或支撑骨架，承受高压差流体冲击。",
      es: "Rigidez estructural calculada, porcentaje de área abierta y marcos de soporte o plisado reforzado.",
      de: "Berechnete strukturelle Steifigkeit, offene Siebflächen und verstärkte Plissier- oder Stützrahmen.",
      fr: "Rigidité structurelle calculée, pourcentages de surface ouverte et plis renforcés ou cadres de support.",
      pt: "Rigidez estrutural calculada, porcentagem de área aberta e estruturas plissadas ou de suporte reforçadas."
    })
  }];
  const imgAlt = pick(lang, {
    en: "Stainless steel woven wire mesh media inspection and micron aperture selection",
    zh: "不锈钢编织丝网介质检测与微米级孔径选型",
    es: "Inspección de medios de malla de alambre tejida de acero inoxidable y selección de apertura micrométrica",
    de: "Prüfung von Edelstahl-Drahtgewebemedien und mikrongenaue Maschenweitenauswahl",
    fr: "Contrôle des médias en toile métallique tissée en acier inoxydable et sélection des ouvertures micrométriques",
    pt: "Inspeção de meios de malha de arame tecida de aço inoxidável e seleção de abertura micrométrica"
  });
  const linkProducts = pick(lang, COMMON.exploreProducts);
  const linkResources = pick(lang, COMMON.relatedResources);
  return <section id="selectionconsiderations" data-nav="light" className="bg-muted/40 text-foreground border-b border-border mf-section">
      <div className="mf-container">
        {/* Header Block */}
        <Reveal>
          <div className="max-w-3xl mb-10 md:mb-12">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-muted text-muted-foreground mf-caption font-medium uppercase tracking-wider mb-3">
              <SlidersHorizontal className="size-3.5 text-primary" />
              <span>{eyebrowLabel}</span>
            </div>
            <h2 className="text-foreground font-default mf-h2">
              {headingText}
            </h2>
            <p className="mt-3 text-muted-foreground leading-relaxed mf-lead">
              {leadText}
            </p>
          </div>
        </Reveal>

        {/* 2-Column Layout: Visual supporting card + Criteria cards */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left Column: Visual Image card */}
          <div className="lg:col-span-5 flex flex-col justify-center">
            <Reveal delay={60}>
              <Card className="overflow-hidden rounded-lg border border-border bg-card text-card-foreground mf-hover-lift">
                <div className="relative mf-card-media mf-ratio-card w-full overflow-hidden">
                  <img src="https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg" alt={imgAlt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono text-foreground flex items-center gap-1.5">
                    <CheckCircle2 className="size-3.5 text-primary" />
                    <span>ASTM E2016 / ISO 9044</span>
                  </div>
                </div>
                <CardContent className="p-5">
                  <div className="flex flex-col gap-1">
                    <h3 className="text-base font-semibold text-foreground tracking-tight">
                      {pick(lang, {
                      en: "Aperture & Geometry Verification",
                      zh: "网孔孔径与几何结构验证",
                      es: "Verificación de apertura y geometría",
                      de: "Prüfung von Maschenweite & Geometrie",
                      fr: "Vérification de l'ouverture et de la géométrie",
                      pt: "Verificação de abertura e geometria"
                    })}
                    </h3>
                    <p className="mf-caption text-muted-foreground leading-relaxed">
                      {pick(lang, {
                      en: "Precise wire diameter, open area ratio, and structural wire locking ensure consistent filtration accuracy under operational loads.",
                      zh: "精准的丝径、开孔率与稳固的经纬锁紧结构，保证运行负载下的稳定过滤精度。",
                      es: "El diámetro de alambre preciso, el área abierta y el bloqueo de alambre estructural aseguran una precisión constante bajo cargas operativas.",
                      de: "Präziser Drahtdurchmesser, offene Siebfläche und stabiler Drahtverbund gewährleisten konstante Filtrationsgenauigkeit unter Last.",
                      fr: "Le diamètre de fil précis, le taux de vide et le verrouillage structurel garantissent une précision constante sous charge opérationnelle.",
                      pt: "O diâmetro de arame preciso, a área aberta e o travamento estrutural garantem precisão constante sob cargas operacionais."
                    })}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          </div>

          {/* Right Column: 4 Criteria Cards */}
          <div className="lg:col-span-7 flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {criteria.map((item, idx) => <Reveal key={idx} delay={Math.min(idx * 60, 300)} className="flex flex-col h-full">
                  <Card className="h-full bg-card border border-border rounded-lg p-5 flex flex-col justify-between mf-hover-lift">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        <h3 className="font-semibold text-foreground mf-h3">
                          {item.title}
                        </h3>
                      </div>
                      <div className="mf-caption font-mono uppercase tracking-wider text-primary mb-2">
                        {item.subtitle}
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </Card>
                </Reveal>)}
            </div>

            {/* Links strip */}
            <Reveal delay={240}>
              <div className="pt-2 flex flex-wrap items-center gap-6">
                <Link to="/ProductsPage" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors">
                  <span>{linkProducts}</span>
                  <ArrowRight className="size-4" />
                </Link>

                <Link to="/TechnicalResourcesPage" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors">
                  <span>{linkResources}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>;
}